import { memo, useCallback, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { formatDateByLocale } from 'utils/formattingUtil';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';

export interface DateFilterProps {
  handleUpdateDateParams: (startDate: string, endDate: string) => void;
  isLocalTime?: boolean;
}

export const DateFilter = memo(
  ({ handleUpdateDateParams, isLocalTime = true }: DateFilterProps) => {
    const urlParams = usePaginationFromURL('orders');

    const startDateFromUrl = urlParams.startDate;
    const endDateFromUrl = urlParams.endDate;

    const currentValue = useMemo(() => {
      if (startDateFromUrl && endDateFromUrl) {
        return JSON.stringify({
          startDate: startDateFromUrl,
          endDate: endDateFromUrl
        });
      }
      return '';
    }, [startDateFromUrl, endDateFromUrl]);

    const { displayLabel, displayParts } = useMemo(() => {
      if (startDateFromUrl && endDateFromUrl) {
        const prefix = 'Date';
        // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
        const extractDate = (dateStr: string) => {
          return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        };

        // Parse dates without timezone conversion
        const start = dayjs(extractDate(startDateFromUrl));
        const end = dayjs(extractDate(endDateFromUrl));

        // If same date, show only one date (single date selection)
        if (start.isSame(end, 'day')) {
          const dateStr = formatDateByLocale(start.toISOString());
          return {
            displayLabel: `${prefix} | ${dateStr}`,
            displayParts: [prefix, dateStr]
          };
        }
        const dateRange = `${formatDateByLocale(start.toISOString())} - ${formatDateByLocale(end.toISOString())}`;
        return {
          displayLabel: `${prefix} | ${dateRange}`,
          displayParts: [prefix, dateRange]
        };
      }
      return {
        displayLabel: 'Pick a Date',
        displayParts: undefined
      };
    }, [startDateFromUrl, endDateFromUrl]);

    const handleUpdateDateParamsRef = useRef(handleUpdateDateParams);
    handleUpdateDateParamsRef.current = handleUpdateDateParams;

    const handleChange = useCallback((value: string) => {
      if (!value) {
        handleUpdateDateParamsRef.current('', '');
        return;
      }

      try {
        const parsed = JSON.parse(value);
        if (parsed.startDate && parsed.endDate) {
          handleUpdateDateParamsRef.current(parsed.startDate, parsed.endDate);
        }
      } catch (error) {
        console.error('Failed to parse date range:', error);
      }
    }, []);

    return (
      <FilterPopover
        label={displayLabel}
        labelParts={displayParts}
        onChange={handleChange}
        selectedItems={currentValue ? [currentValue] : []}
        filterType={FilterType.DATE_PICKER}
        hideIndicator
        isLocalTime={isLocalTime}
      />
    );
  }
);

DateFilter.displayName = 'DateFilter';
